<template>
  <div class="bg">
    <v-row justify="center">
      <div class="white-card pa-8">
        <v-row justify="center">
          <img height="150px" class="my-8" src="../assets/images/logo.png" alt="" />
        </v-row>
        <v-row justify="center">
          <p class="welcome-text fs400">WELCOME TO</p>
        </v-row>
        <v-row class="ma-0" justify="center">
          <h1 class="blue-color text-center mt-4">LOTUS LIFE DASHBOARD</h1>
        </v-row>
        <v-row justify="center">
          <p class="info-text fs700">DOCTOR/CLIENT MANAGEMENT SYSTEM</p>
        </v-row>
        <v-row class="ma-0" justify="center">
          <v-text-field v-model="email" class="mt-12" rounded filled label="EMAIL ADDRESS"></v-text-field>
        </v-row>
        <v-row class="ma-0" justify="center">
          <v-text-field v-model="password" rounded filled label="PASSWORD"
            :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'" :type="show ? 'text' : 'password'"
            @click:append="show = !show"></v-text-field>
        </v-row>
        <v-row class="ma-0" justify="center">
          <v-btn @click="login()" color="#e022d1" dark rounded width="240px" class="pink-btn">
            LOGIN
          </v-btn>
        </v-row>
        <v-row class="ma-0" justify="center">
          <v-btn @click="$router.push('/register')" color="#4390c0" dark rounded width="240px" class="mt-4">
            REGISTER
          </v-btn>
        </v-row>
        <v-row justify="center">
          <h4 @click="$router.push('/forgotPassword')" class="black-color mt-4 pointer">
            Forgot Password?
          </h4>
        </v-row>
      </div>
    </v-row>
    <v-row justify="center">
      <v-overlay :value="loading">
        <v-progress-circular :size="100" color="#4390c0" indeterminate></v-progress-circular>
      </v-overlay>
    </v-row>
    <v-dialog v-model="promptDialog" max-width="480">
      <v-card class="pa-8" elevation="4" shaped style="border-radius: 24px">
        <v-card-text class="text-center">
          <v-icon size="96" color="red" v-if="promptType == 'error'">
            mdi-alert-circle
          </v-icon>
          <v-icon size="96" color="green" v-if="promptType == 'success'">
            mdi-check-circle
          </v-icon>
          <div class="text-h5 my-6" v-if="promptType == 'error'">Error</div>
          <div class="text-h5 my-6" v-if="promptType == 'success'">Success</div>
          <div class="body-1 text-h6">{{ promptMessage }}</div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn color="black" dark depressed large style="border-radius: 24px" @click="promptDialog = false">
            CLOSE
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { auth } from "../../firebaseConfig.js";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
} from "firebase/auth";
import { getUserProfileByAuth, logAction } from "@/utils/firestoreUtil";

export default {
  data() {
    return {
      email: null,
      password: null,
      loading: false,
      promptDialog: false,
      promptMessage: "",
      promptType: "",
      show: null,
      authUnsub: null,
    };
  },
  async created() {
    this.authUnsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        this.email = user.email;
        this.loginAuto();
      }
    });
  },
  beforeDestroy() {
    if (this.authUnsub) {
      this.authUnsub();
      this.authUnsub = null;
    }
  },
  methods: {
    // 1️⃣ Auth only
    async LoginAuth() {
      try {
        await setPersistence(auth, browserLocalPersistence);
        this.loading = true;
        localStorage.removeItem("lastViewedUserType");
        const email = (this.email || "").trim().toLowerCase();
        this.email = email;
        await signInWithEmailAndPassword(auth, email, this.password);
        this.loading = false;
        return {};
      } catch (error) {
        this.loading = false;
        console.error("LoginAuth error:", error.code, error.message);
        const invalidCreds = [
          "auth/wrong-password",
          "auth/user-not-found",
          "auth/invalid-login-credentials",
          "auth/invalid-credential",
        ].includes(error.code);
        const msg = invalidCreds
          ? "Email or Password is incorrect, please try again!"
          : "Something went wrong, please try again!";
        return { error: msg };
      }
    },
    // 2️⃣ Auto‐login: resolve profile by email, then authID fallback
    async loginAuto() {
      try {
        this.loading = true;

        const authUser = auth.currentUser;
        const tempUser = await getUserProfileByAuth({
          email: this.email || (authUser && authUser.email),
          uid: authUser && authUser.uid,
        });

        if (tempUser) {
          // Backfill email if the Firestore doc was missing it
          if (!tempUser.email && authUser && authUser.email) {
            tempUser.email = authUser.email;
          }

          this.$store.commit("setUser", tempUser);
          this.email = tempUser.email || this.email;

          await this.RecordLogin();

          this.promptMessage = "Logged in successfully!";
          this.promptType = "success";
          this.promptDialog = true;
          this.loading = false;
          setTimeout(() => {
            this.promptDialog = false;
            this.$router.replace({ path: "/userManagement" }).catch(() => { });
          }, 3000);
          return;
        }

        // C) No matching user found
        this.loading = false;
        this.promptMessage = "No account found for that email.";
        this.promptType = "error";
        this.promptDialog = true;
        await auth.signOut();
      } catch (err) {
        console.error("Login error:", err);
        this.loading = false;
        this.promptMessage =
          "An error occurred while logging in. Please try again!";
        this.promptType = "error";
        this.promptDialog = true;
        await auth.signOut();
      }
    },
    // 3️⃣ Manual login flow: Auth → profile lookup
    async login() {
      try {
        const authRes = await this.LoginAuth();
        if (authRes.error) {
          this.promptMessage = authRes.error;
          this.promptType = "error";
          this.promptDialog = true;
          return;
        }
      } catch (err) {
        console.error("Login error:", err);
        this.loading = false;
        this.promptMessage =
          "An error occurred while logging in. Please try again!";
        this.promptType = "error";
        this.promptDialog = true;
        await auth.signOut();
      }
    },
    async RecordLogin() {
      await logAction("Login/Logout", "Logged In", this.email, this.email + " has logged in to the dashboard");
    },
  },
};
</script>

<style scoped>
.bg {
  width: 100%;
  min-height: 101.3vh;
  background-image: url("../assets/images/background.png");
  background-size: cover;
  background-attachment: fixed;
  /* Keeps background fixed in place */
  background-position: center center;
}

.white-card {
  width: 500px;
  max-width: 80%;
  border-radius: 0px 0px 25px 25px;
  box-shadow: 1px 1px 4px grey;
  background-color: white;
}

.text-center {
  text-align: center;
}

.welcome-text {
  font-size: 25px;
  font-weight: 300;
}

.info-text {
  font-size: 16px;
  font-weight: 400;
}

.input {
  width: 250px !important;
}
</style>
